import {
  combineReducers,
  configureStore,
  createListenerMiddleware,
  type Reducer,
  type UnknownAction,
} from '@reduxjs/toolkit'
import { persistReducer, persistStore } from 'redux-persist'
import { createEncryptedStorage } from '@quickmd-llc/frontend-utils/secure-storage'
import { PERSIST, PURGE, REHYDRATE } from 'redux-persist/es/constants'
import { createStateSyncMiddleware, initMessageListener } from 'redux-state-sync'
import homeReducer from '../store/home/HomeState'
import authReducer from './authState'
import managementReducer from '../store/management/ManagementReducer'
import memberReducer from '../store/member/MemberReducer'
import intakeFormsReducer from '../store/intakeForms/IntakeFormsReducer'
import availabilityReducer from '../store/availability/AvailabilityReduser'
import calendarPageReducer from '../store/calendar/CalendarReducer'
import chartsReducer from '../store/charts/ChartsReducer'
import viewServiceReducer from '../store/viewService/ViewServiceReducer'
import newServiceReducer from '../store/newService/NewServiceReducer'
import newMemberReducer from '../store/newMember/newMemberReducer'
import visitsReducer from '../store/visits/VisitsReducer'
import pastVisitReducer from '../store/pastVisit/PastVisitReducer'
import newVisitReducer from '../store/new-visit/NewVisitReducer'
import { successEventEmitterMiddleware } from '../middlewares/SuccessEventEmitterMiddleware'
import accountSettingsReducer from '../store/accountSettings/AccountSettingsReducer'
import forgotPasswordReducer from '../store/forgotPassword/forgotPasswordReducer'
import resetPasswordReducer from '../store/resetPassword/resetPasswordReducer'
import { visitsPageBlackList } from './syncBlackLists/visitsPageBlackList'
import { accountSettingsBlackList } from './syncBlackLists/accountSettingsBlackList'
import { availabilitiesPageBlackList } from './syncBlackLists/availabilitiesPageBlackList'
import { chartsBlackList } from './syncBlackLists/chartsBlackLIst'
import { managementBlackList } from './syncBlackLists/managementBlackList'
import { calendarPageBlackList } from './syncBlackLists/calendarPageBlackList'
import drugRulesSettingsReducer from '../store/drugRulesSettings/drugRulesSettingsReducer'
import { timeZoneReducer } from '../store/timeZone/timeZoneReducer'
import { filePopUpConstantsBlackList } from '../store/file-pop-up/FilePopUpConstants'
import { saveSettingsBlackList } from '../store/state-settings/StateSettingsConstants'
import flagsReducer from '../store/flags/FlagsReducer'
import { idVerificationPopUpConstantsBlackList } from '../store/id-verification-pop-up/IDVerificationPopUpConstants'
import lookupsReducer from '../store/lookups/lookupsReducer'
import { newVisitBlackList } from '../store/new-visit/NewVisitConstants'
import { SET_FLAGS } from '../store/flags/FlagsConstants'
import { logoutTeardownMiddleware } from './logoutTeardownMiddleware'
import { setPersistor } from './persistorRegistry'

const listenerMiddleware = createListenerMiddleware()
const LISTENER = 'listenerMiddleware/add'
const channel = 'QMD2-Admin'

const rootReducer = combineReducers({
  home: homeReducer,
  auth: authReducer,
  management: managementReducer,
  newService: newServiceReducer,
  viewService: viewServiceReducer,
  forgotPassword: forgotPasswordReducer,
  resetPassword: resetPasswordReducer,
  newClinicMember: newMemberReducer,
  member: memberReducer,
  accountSettings: accountSettingsReducer,
  intakeForms: intakeFormsReducer,
  availabilityPage: availabilityReducer,
  drugRulesSettingsReducer,
  calendarPageReducer,
  chartsReducer,
  visitsReducer,
  pastVisitReducer,
  newVisitReducer,
  timeZoneReducer,
  flags: flagsReducer,
  lookups: lookupsReducer,
})

type RootState = ReturnType<typeof rootReducer>

const persistConfig = {
  key: 'root',
  // Encrypted rather than `redux-persist/lib/storage`, which writes plain JSON. What
  // lands in `persist:root` here is *other people's* records — patient lists,
  // `patientInfo`, printed chart info, past-visit notes and prescriptions — on a
  // workstation shared across a clinic. The person who reads it opens DevTools, not a
  // disassembler. It sits at the storage-adapter layer rather than in `transforms`
  // because transforms are synchronous and WebCrypto is not. See QMD-4332.
  storage: createEncryptedStorage(),
  blacklist: ['home', 'newService', 'forgotPassword', 'resetPassword', 'newClinicMember', 'clinicMembers'],
}

const reduxStateSyncConfig = {
  predicate: (action: { type: string } | ((...args: unknown[]) => unknown)) => {
    const blacklist = [
      PERSIST,
      PURGE,
      REHYDRATE,
      LISTENER,
      ...visitsPageBlackList,
      ...calendarPageBlackList,
      ...availabilitiesPageBlackList,
      ...accountSettingsBlackList,
      ...managementBlackList,
      ...chartsBlackList,
      ...filePopUpConstantsBlackList,
      ...saveSettingsBlackList,
      ...idVerificationPopUpConstantsBlackList,
      ...newVisitBlackList,
      // Every tab reads Remote Config for itself, so relaying this only re-renders the other
      // tabs with a value they already computed — and a flag pinned in one tab via DevTools
      // must not leak into another.
      SET_FLAGS,
    ]
    if (typeof action !== 'function') {
      if (Array.isArray(blacklist)) {
        return !blacklist.includes(action.type)
      }
    }
    return false
  },
  channel,
}

const persistedReducer = persistReducer(
  persistConfig,
  // TEMP SUPPRESSION/TS ASSERTION: CRA -> VITE
  rootReducer as unknown as Reducer<RootState, UnknownAction>,
)

export const store = configureStore({
  reducer: persistedReducer,
  // @ts-expect-error TS2351 TEMP ERROR SUPPRESSION: CRA -> VITE
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
      immutableCheck: false,
    })
      .concat(logoutTeardownMiddleware)
      .prepend(successEventEmitterMiddleware)
      .prepend(listenerMiddleware.middleware)
      .prepend(
        createStateSyncMiddleware({
          ...reduxStateSyncConfig,
        }),
      ),
})

initMessageListener(store)

// Lives here rather than in `main.tsx` so the logout teardown can reach it without
// importing the React tree. See QMD-4332.
export const persistor = persistStore(store)
setPersistor(persistor)

export type QuickMdState = ReturnType<typeof store.getState>
export type QuickMdDispatch = typeof store.dispatch
